import { IForegroundSize, IImageSize, ImageRelativeResizeType, INextWH, IOriginalSize, IPictureSrcRect, ISizeInfo } from './image-size.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IPictureModelStyle } from '../../Interface/model-props/picture-model-props.interface';
import ImageUrl from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-url';
import { transaction } from '@src/slide/domain/slide-app';
import isEqual from 'lodash/isEqual';
import ImageOriginalSizeWithDPI from '@src/slide/infrastructure/module/dependent/image/original-size-with-dpi';
import { getAttachmentDefaultSizeByRatio } from '@src/slide/domain/ui-model/element-modeles/property/attachment/utils/size';

const MIN = 0;
const DEFAULT_VALUE = 1;
const DEFAULT_RECT_VALUE = 0;

export default class ImageSize implements IImageSize {
    private model: IPictureModel;

    public constructor(model: IPictureModel) {
        this.model = model;
    }

    public getRelativeResize(): ImageRelativeResizeType {
        return (this.model.isImage() && this.model?.props.relativeResize !== ImageRelativeResizeType.noRelative)
            ? ImageRelativeResizeType.isRelative
            : ImageRelativeResizeType.noRelative;
    }

    public handleRelativeResize(value: number | boolean): void {
        this.model.updateProps({ relativeResize: value
            ? ImageRelativeResizeType.isRelative
            : ImageRelativeResizeType.noRelative,
        });
    }

    /**
     * 重置大小
     */
    public resetSize = (): void => {
        const relId = this.model.sdkData?.props?.blipFill?.blip?.embed || '';
        const originalSize = ImageOriginalSizeWithDPI.getAndUpdateImageOriginalSize(
            relId, this.model.props.src,
            (originalSize) => {
                this.updateNextSize(originalSize.width, originalSize.height);
            },
        );
        if (originalSize) {
            this.updateNextSize(originalSize.width, originalSize.height);
        }
    };

    public getOriginalSize = (): IOriginalSize => {
        const relId = this.model.sdkData?.props?.blipFill?.blip?.embed;
        return ImageOriginalSizeWithDPI.getImageOriginalSize(relId || '')
            ?? ImageUrl.getImageSizeFromUrl(this.model.props.src);
    };

    public getForegroundImageSize(props?: ISizeInfo): IForegroundSize {
        const { style } = this.model.props;
        const fixedProps = { ...style, ...props };
        const { width, height, srcRect } = fixedProps;
        let pl = MIN; let pr = MIN; let pt = MIN; let pb = MIN;
        let rw = width; let rh = height;
        if (!srcRect) {
            return { pl, pr, pt, pb, rw, rh };
        }
        const { l = DEFAULT_RECT_VALUE, r = DEFAULT_RECT_VALUE,
            t = DEFAULT_RECT_VALUE, b = DEFAULT_RECT_VALUE } = srcRect;
        // 当经过裁剪后，图片前景宽度/高度为0时，需要进行特殊处理
        const isMinW = Math.round((1 - l - r) * 100) / 100 === MIN;
        pl = isMinW ? -width / 2 : (width * l) / (1 - l - r);
        pr = isMinW ? -width / 2 : (width * r) / (1 - l - r);
        rw = width + pl + pr;
        const isMinH = Math.round((1 - t - b) * 100) / 100 === MIN;
        pt = isMinH ? -height / 2 : (height * t) / (1 - t - b);
        pb = isMinH ? -height / 2 : (height * b) / (1 - t - b);
        rh = height + pt + pb;
        return { pl, pr, pt, pb, rw, rh };
    }

    public updateForegroundImageSize(props: Partial<IForegroundSize>): void {
        const { style } = this.model.props;
        const { width, height } = style;
        let { srcRect } = style;
        const { pl, pt, rw, rh } = { ...this.getForegroundImageSize(), ...props };
        const pr = props.pr ?? (rw - width - pl);
        const pb = props.pb ?? (rh - height - pt);
        // 对齐office，当前景宽度为0时，l=0,r=1；高度为0时，t=0,b=1
        srcRect = {
            l: rw === MIN ? MIN : pl / rw,
            r: rw === MIN ? DEFAULT_VALUE : pr / rw,
            t: rh === MIN ? MIN : pt / rh,
            b: rh === MIN ? DEFAULT_VALUE : pb / rh,
        };
        this.model.updateProps({ style: { srcRect } });
    }

    /**
     * 图片重置，计算新的 width、height、left、top
     * @returns 新的宽高相关属性
     */
    private updateNextSize(_width: number, _height: number) {
        // 计算 left、top
        // - 默认没有裁剪，left/top 没有变化
        // - 如果有裁剪，需要重新倒推计算 left 和 top
        // 特殊逻辑处理：组合中的图片，left 和 top 是相对于组合的，而不是幻灯片！！！
        const { style } = this.model.props;
        const { nextLeft: left, nextTop: top } = (!this.model.rootGroupModel
            && style.srcRect && !this.judgeObjIsZero(style.srcRect))
            ? this.computeNextLeftTop(style, _width, _height)
            : { nextLeft: style.left || 0, nextTop: style.top || 0 };
        // 如果是附件(如视频封面)的重置，需要根据比例重新计算图片的宽高；如果不是附件，该函数则返回入参的宽高。
        const { width, height } = getAttachmentDefaultSizeByRatio(this.model, _width, _height);
        const newRect = { left, top, width, height, rotate: 0 };
        const frame = this.model.getFrame().getFrameInfo();
        const oldRect = {};
        Object.keys(newRect).forEach((key) => {
            oldRect[key] = frame?.[key];
        });
        const srcRect = { l: 0, r: 0, t: 0, b: 0 };
        if (isEqual(oldRect, newRect) && isEqual(srcRect, this.model.props.style.srcRect)) return;
        transaction(() => {
            this.model.getFrame().updateFrame({ frame: newRect });
            this.model.updateProps({ style: { srcRect } });
        });
    }


    /**
     * 根据新的宽高倒推新的 left 和 top
     * @param {*} style 图片信息
     * @param {*} nextWidth 新的宽度
     * @param {*} nextHeight 新的高度
     */
    private computeNextLeftTop(style: IPictureModelStyle, nextWidth: number, nextHeight: number) {
        const l = style.srcRect?.l ?? 0;
        const t = style.srcRect?.t ?? 0;
        const { left, top } = style;

        return {
            nextLeft: left - (l * nextWidth),
            nextTop: top - (t * nextHeight),
        };
    }

    /**
     * 判断对象是否为零
     * @param {*} obj 要判断的对象
     */
    private judgeObjIsZero(obj?: IPictureSrcRect): boolean {
        if (!obj) return false;
        return Object.values(obj).every(val => val === 0);
    }
}
